import { useTranslation } from 'react-i18next';

import { useEffect } from 'react';

import { Bold } from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import CharacterCount from '@tiptap/extension-character-count';
import Code from '@tiptap/extension-code';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import { Italic } from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import { Text as TipTapText } from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import {
  Editor as TiptapEditor,
  JSONContent as TiptapJSONContent,
  useEditor as useTiptapEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import * as css from './Editor.css';
import { CustomLink } from './Extensions/Link/Link';
import { CustomMention } from './Extensions/Mentions/Mentions';
import { Mention } from './Extensions/Mentions/helpers';

export type Editor = TiptapEditor | undefined;
export type JSONContent = TiptapJSONContent;
export type Content = {
  html: string;
  json: JSONContent;
  text: string;
};

type DocumentEditorProps = {
  content?: JSONContent;
  onBlur?: (editor) => void;
  onChange?: (editor) => void;
  onEditorReady?: (editor: Editor) => void;
  onMentionsSearch?: (search: string) => Promise<Mention[]>;
  version?: 'readonly' | 'editable';
  placeholder?: string;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watchedAtttributes?: any[];
};

export const useEditor = ({
  content,
  onBlur,
  onChange,
  onEditorReady,
  onMentionsSearch,
  version = 'editable',
  placeholder,
  className,
  watchedAtttributes,
}: DocumentEditorProps) => {
  const { t } = useTranslation();
  const editor = useTiptapEditor(
    {
      extensions: [
        Bold,
        BulletList,
        CharacterCount.configure({
          mode: 'nodeSize',
          limit: 30000,
        }),
        Code,
        ...(onMentionsSearch ? [CustomMention({ handleSearch: onMentionsSearch })] : []),
        CustomLink,
        Heading,
        Italic,
        ListItem,
        OrderedList,
        Highlight.configure({
          HTMLAttributes: {
            class: css.highlight,
          },
        }),
        Paragraph,
        Placeholder.configure({
          placeholder:
            placeholder ||
            t('components-posts-placeholder_whatsOnYourMind', "What's on your mind?"),
          emptyEditorClass: css.emptyEditor,
        }),
        StarterKit.configure({
          dropcursor: false,
        }),
        TipTapText,
        Underline,
      ],
      content,
      editable: version !== 'readonly',
      editorProps: {
        attributes: {
          class: `${css.editor} ${className || ''}`,
        },
      },
    },
    watchedAtttributes || []
  );

  useEffect(() => {
    if (!editor) return;

    const handleBlur = () => {
      if (onBlur)
        onBlur({
          html: editor?.getHTML(),
          json: {
            ...editor.getJSON(),
            version: '2.12.0',
          },
          text: editor?.getText(),
        });
    };

    const handleChange = () => {
      if (onChange)
        onChange({
          html: editor?.getHTML(),
          json: {
            ...editor.getJSON(),
            version: '2.12.0',
          },
          text: editor?.getText(),
        });
    };

    if (editor && onEditorReady) {
      onEditorReady(editor);
    }

    editor.on('blur', handleBlur);
    editor.on('update', handleChange);
  }, [editor]);

  return {
    editor,
    content: {
      length: editor?.storage?.characterCount?.characters(),
    },
  };
};
